import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
  ReactNode,
  Dispatch,
  SetStateAction,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useAppDispatch, useAppSelector } from "../Redux/hooks";
import { RootState } from "../store";
import { useAuth } from "./AuthContext";
import {
  getCart,
  addToCart,
  updateCartItem,
  removeFromCart,
  clearCart,
  applyVoucher,
  applySalarCode,
  removeSalarCode,
  applyCoupon,
  removeCoupon,
  applyReferral,
  removeReferral,
  clearCartState,
  getSavedForLater,
  saveForLaterItem,
  moveSavedToCart,
  removeSavedForLaterItem,
} from "../Redux/custmoer_cart_service/slice";
import {
  Cart,
  CartStatus,
  CartType,
  CouponType,
  AddToCartRequest,
  CartItem,
  ProductType,
} from "../Redux/custmoer_cart_service/types";

interface CartContextType {
  cart: Cart | null;
  cartItems: CartItem[];
  loading: boolean;
  error: string | null;
  success: boolean;
  message: string | null;
  cartCount: number;
  cartTotal: number;
  cartOpen: boolean;
  setCartOpen: Dispatch<SetStateAction<boolean>>;
  isCartOpen: boolean;
  setIsCartOpen: Dispatch<SetStateAction<boolean>>;
  fetchCart: (cartType?: CartType) => void;
  addItemToCart: (data: AddToCartRequest, cartType?: CartType) => Promise<any>;
  addToCart: (data: any, cartType?: CartType) => Promise<any>;
  updateItemQuantity: (itemId: string, quantity: number, cartType?: CartType) => void;
  removeItemFromCart: (itemId: string, cartType?: CartType) => void;
  applyVoucherCode: (voucherId: string, cartType?: CartType) => void;
  applyCouponCode: (code: string, type?: CouponType, cartType?: CartType) => void;
  removeCouponCode: (cartType?: CartType) => void;
  applyReferralCode: (referralId: string, cartType?: CartType) => void;
  removeReferralCode: (cartType?: CartType) => void;
  applySalarCodeValue: (code: string, commissionType?: string, cartType?: CartType) => void;
  removeSalarCodeValue: (cartType?: CartType) => void;
  clearCartItems: (cartType?: CartType) => void;
  clearMessage: () => void;
  isProductInCart: (productId: string | number, variantId?: string) => boolean;
  savedForLaterItems: CartItem[];
  savedForLaterCount: number;
  fetchSavedForLater: (cartType?: CartType) => void;
  saveItemForLater: (itemId: string, cartType?: CartType) => Promise<any>;
  moveSavedItemToCart: (itemId: string, cartType?: CartType) => Promise<any>;
  removeSavedItem: (itemId: string) => Promise<any>;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { cart, savedForLaterItems, loading, error, success, message } = useAppSelector(
    (state: RootState) => state.customerCart
  );
  const [cartOpen, setCartOpen] = useState<boolean>(false);

  const [localItems, setLocalItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem("local_cart_items");
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [removedItemIds, setRemovedItemIds] = useState<string[]>([]);

  const cartItems = useMemo(() => {
    const isExcluded = (item: CartItem) => {
      const q = item.quantity ?? 1;
      if (q <= 0) return true;
      if (removedItemIds.includes(item.id)) return true;
      if (removedItemIds.includes(item.productId)) return true;
      if (item.variantId && removedItemIds.includes(`${item.productId}_${item.variantId}`)) return true;
      if (removedItemIds.includes(`${item.productId}_default`)) return true;
      return false;
    };

    const serverItems = (cart?.items ?? []).filter(
      (item) => !isExcluded(item) && !item.savedForLater
    );
    const itemsMap = new Map<string, CartItem>();
    serverItems.forEach((item) => {
      const key = `${item.productId}_${item.variantId || ""}`;
      itemsMap.set(key, item);
    });
    localItems.filter((item) => !isExcluded(item)).forEach((item) => {
      const key = `${item.productId}_${item.variantId || ""}`;
      if (!itemsMap.has(key)) {
        itemsMap.set(key, item);
      }
    });
    return Array.from(itemsMap.values());
  }, [cart, localItems, removedItemIds]);

  const cartCount = useMemo(() => {
    if (cartItems.length > 0) {
      return cartItems.reduce((total, item) => total + (item.quantity || 1), 0);
    }
    if (cart && typeof cart.itemsCount === "number" && cart.itemsCount > 0) {
      return cart.itemsCount;
    }
    return 0;
  }, [cart, cartItems]);

  const cartTotal = useMemo(() => {
    if (cartItems.length > 0) {
      return cartItems.reduce((total, item) => {
        const price = Number(
          item.finalPrice ??
          item.totalPrice ??
          (item.unitPrice * (item.quantity || 1))
        );
        return total + (isNaN(price) ? 0 : price);
      }, 0);
    }
    if (cart && cart.grandTotal !== undefined && cart.grandTotal !== null) {
      const grandTotalNum = Number(cart.grandTotal);
      if (!isNaN(grandTotalNum) && grandTotalNum > 0) {
        return grandTotalNum;
      }
    }
    return 0;
  }, [cart, cartItems]);

  const effectiveCart = useMemo(() => {
    if (!cart && cartItems.length === 0) {
      return null;
    }

    const computedTotal = cartTotal;

    return {
      ...(cart || {}),
      id: cart?.id || "local-cart",
      userId: cart?.userId || "",
      cartType: cart?.cartType || CartType.ECOMMERCE,
      status: cart?.status || CartStatus.ACTIVE,
      referralId: cart?.referralId ?? null,
      invitationCode: cart?.invitationCode ?? null,
      selectedAddressId: cart?.selectedAddressId ?? null,
      sessionId: cart?.sessionId ?? null,
      couponId: cart?.couponId ?? null,
      couponCode: cart?.couponCode ?? null,
      couponType: cart?.couponType ?? null,
      couponDiscountAmount: cart?.couponDiscountAmount || 0,
      couponDiscountPercentage: cart?.couponDiscountPercentage ?? null,
      couponMaxDiscount: cart?.couponMaxDiscount ?? null,
      couponAppliedAt: cart?.couponAppliedAt ?? null,
      salarCode: cart?.salarCode ?? null,
      salarCodeUserId: cart?.salarCodeUserId ?? null,
      salarCodeCommissionType: cart?.salarCodeCommissionType ?? null,
      salarCodeDiscountAmount: cart?.salarCodeDiscountAmount || 0,
      salarCodeAppliedAt: cart?.salarCodeAppliedAt ?? null,
      voucherId: cart?.voucherId ?? null,
      voucherType: cart?.voucherType ?? null,
      voucherAppliedAmount: cart?.voucherAppliedAmount || 0,
      voucherAvailableBalance: cart?.voucherAvailableBalance ?? null,
      voucherAppliedAt: cart?.voucherAppliedAt ?? null,
      deliveryOptionId: cart?.deliveryOptionId ?? null,
      deliveryOptionName: cart?.deliveryOptionName ?? null,
      deliveryOptionPrice: cart?.deliveryOptionPrice || 0,
      deliveryEstimatedDays: cart?.deliveryEstimatedDays ?? null,
      deliveryIsFree: cart?.deliveryIsFree ?? false,
      items: cartItems,
      itemsCount: cartCount,
      itemsTotal:
        cart?.itemsTotal && cart.itemsTotal > 0 && cart.items && cart.items.length === cartItems.length
          ? cart.itemsTotal
          : computedTotal,
      subtotal:
        cart?.subtotal && cart.subtotal > 0 && cart.items && cart.items.length === cartItems.length
          ? cart.subtotal
          : computedTotal,
      deliveryCharges: cart?.deliveryCharges || 0,
      platformFee: cart?.platformFee || 0,
      taxAmount: cart?.taxAmount || 0,
      gstAmount: cart?.gstAmount || 0,
      grandTotal:
        cart?.grandTotal && cart.grandTotal > 0 && cart.items && cart.items.length === cartItems.length
          ? cart.grandTotal
          : computedTotal,
      savedAmount: cart?.savedAmount || 0,
      salarCoinsEarned: cart?.salarCoinsEarned || 0,
      totalDiscount: cart?.totalDiscount || 0,
      metadata: cart?.metadata ?? null,
      expiresAt: cart?.expiresAt ?? null,
      lastActivityAt: cart?.lastActivityAt || new Date().toISOString(),
      createdAt: cart?.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    } as Cart;
  }, [cart, cartItems, cartCount, cartTotal]);

  useEffect(() => {
    if (!isAuthenticated) return;
    dispatch(getCart(CartType.ECOMMERCE));
    dispatch(getSavedForLater(CartType.ECOMMERCE));
  }, [dispatch, isAuthenticated]);

  const fetchCart = (cartType: CartType = CartType.ECOMMERCE) => {
    if (!isAuthenticated) return;
    dispatch(getCart(cartType));
  };

  const addItemToCart = async (data: AddToCartRequest, cartType: CartType = CartType.ECOMMERCE) => {
    if (!isAuthenticated) {
      toast.info("Please sign in to add items to your cart.", {
        toastId: "cart-signin-required",
      });
      navigate("/SignIn");
      return null;
    }

    const variantKey = data.variantId || "default";
    const localId = `${data.productId}_${variantKey}`;
    const newCartItem: CartItem = {
      id: localId,
      cartId: cart?.id || "local-cart",
      productId: data.productId,
      productName: data.productName,
      productImage: data.productImage || null,
      productType: data.productType || ProductType.PHYSICAL,
      variantId: data.variantId || null,
      variantName: data.variantName || null,
      quantity: data.quantity || 1,
      unitPrice: data.unitPrice || 0,
      totalPrice: (data.unitPrice || 0) * (data.quantity || 1),
      discountAmount: 0,
      finalPrice: (data.unitPrice || 0) * (data.quantity || 1),
      sellerId: data.sellerId || null,
      sellerName: data.sellerName || null,
      referralId: data.referralId || null,
      isMlmProduct: Boolean(data.isMLMProduct),
      metadata: data.metadata || null,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };

    setLocalItems((prev) => {
      const existingIdx = prev.findIndex(
        (i) => i.productId === data.productId && (i.variantId || null) === (data.variantId || null)
      );
      let updated: CartItem[];
      if (existingIdx > -1) {
        updated = [...prev];
        const existing = updated[existingIdx];
        const newQty = (existing.quantity || 1) + (data.quantity || 1);
        updated[existingIdx] = {
          ...existing,
          quantity: newQty,
          totalPrice: newQty * (existing.unitPrice || 0),
          finalPrice: newQty * (existing.unitPrice || 0),
        };
      } else {
        updated = [newCartItem, ...prev];
      }
      try {
        localStorage.setItem("local_cart_items", JSON.stringify(updated));
      } catch {}
      return updated;
    });

    setRemovedItemIds((prev) =>
      prev.filter(
        (id) =>
          id !== localId &&
          id !== data.productId &&
          (data.variantId ? id !== `${data.productId}_${data.variantId}` : true)
      )
    );

    setCartOpen(true);

    try {
      const res = await dispatch(addToCart({ data, cartType }));
      return res;
    } catch (err) {
      console.warn("Backend add to cart error:", err);
      return null;
    }
  };

  const addToCartAction = async (
    item: any,
    cartType: CartType = CartType.ECOMMERCE
  ) => {
    const request: AddToCartRequest = {
      productId: String(item.productId || item._id || item.id || ""),
      productName: String(item.productName || item.title || item.name || "Product"),
      productImage: item.productImage || item.image || item.mainImage || undefined,
      productType: item.productType || ProductType.PHYSICAL,
      variantId: item.variantId || item.variant || undefined,
      variantName: item.variantName || item.variant || item.color || undefined,
      quantity: Math.max(1, Number(item.quantity) || 1),
      unitPrice: Number(item.unitPrice !== undefined ? item.unitPrice : (item.price || 0)),
      sellerId: item.sellerId || undefined,
      sellerName: item.sellerName || undefined,
      referralId: item.referralId || undefined,
      isMLMProduct: Boolean(item.isMLMProduct),
      isAdminProduct: item.isAdminProduct !== undefined ? Boolean(item.isAdminProduct) : undefined,
      metadata: item.metadata || (item.mrp || item.color || item.size || item.description ? {
        mrp: item.mrp,
        color: item.color,
        size: item.size,
        description: item.description,
      } : undefined),
    };

    return addItemToCart(request, cartType);
  };

  const updateItemQuantity = (itemId: string, quantity: number, cartType: CartType = CartType.ECOMMERCE) => {
    if (quantity <= 0) {
      removeItemFromCart(itemId, cartType);
      return;
    }

    const targetItem = cartItems.find(
      (i) =>
        i.id === itemId ||
        i.productId === itemId ||
        (i.variantId && i.variantId === itemId) ||
        `${i.productId}_${i.variantId || "default"}` === itemId
    );
    const prodId = targetItem?.productId || itemId;
    const varId = targetItem?.variantId || null;
    const localKey = `${prodId}_${varId || "default"}`;
    const targetItemIds = new Set(
      [itemId, targetItem?.id, prodId, localKey].filter(Boolean) as string[]
    );

    setLocalItems((prev) => {
      const updated = prev.map((item) => {
        if (
          targetItemIds.has(item.id) ||
          targetItemIds.has(item.productId) ||
          (item.productId === prodId && (item.variantId || null) === varId)
        ) {
          const unitPrice = item.unitPrice || 0;
          return {
            ...item,
            quantity,
            totalPrice: unitPrice * quantity,
            finalPrice: unitPrice * quantity,
          };
        }
        return item;
      });
      try {
        localStorage.setItem("local_cart_items", JSON.stringify(updated));
      } catch {}
      return updated;
    });

    const serverItem = (cart?.items || []).find(
      (si) =>
        si.id === itemId ||
        targetItemIds.has(si.id) ||
        (si.productId === prodId && (si.variantId || null) === varId)
    );

    if (serverItem && serverItem.id) {
      dispatch(updateCartItem({ itemId: serverItem.id, data: { quantity }, cartType }))
        .unwrap()
        .catch(() => {
          dispatch(clearCartState());
        });
    }
  };

  const removeItemFromCart = (itemId: string, cartType: CartType = CartType.ECOMMERCE) => {
    const targetItem = cartItems.find(
      (i) =>
        i.id === itemId ||
        i.productId === itemId ||
        (i.variantId && i.variantId === itemId) ||
        `${i.productId}_${i.variantId || "default"}` === itemId
    );
    const prodId = targetItem?.productId || itemId;
    const varId = targetItem?.variantId || null;
    const localKey = `${prodId}_${varId || "default"}`;
    const targetItemIds = new Set(
      [itemId, targetItem?.id, prodId, localKey].filter(Boolean) as string[]
    );

    setRemovedItemIds((prev) => {
      const next = new Set(prev);
      targetItemIds.forEach((id) => next.add(id));
      return Array.from(next);
    });

    setLocalItems((prev) => {
      const updated = prev.filter(
        (item) =>
          !targetItemIds.has(item.id) &&
          !targetItemIds.has(item.productId) &&
          !(item.productId === prodId && (item.variantId || null) === varId)
      );
      try {
        localStorage.setItem("local_cart_items", JSON.stringify(updated));
      } catch {}
      return updated;
    });

    const serverItem = (cart?.items || []).find(
      (si) =>
        si.id === itemId ||
        targetItemIds.has(si.id) ||
        (si.productId === prodId && (si.variantId || null) === varId)
    );

    if (serverItem && serverItem.id) {
      dispatch(removeFromCart({ itemId: serverItem.id, cartType }))
        .unwrap()
        .catch(() => {
          dispatch(clearCartState());
        });
    } else {
      dispatch(clearCartState());
    }
  };

  const applyVoucherCode = (voucherId: string, cartType: CartType = CartType.ECOMMERCE) => {
    dispatch(applyVoucher({ data: { voucherId }, cartType }));
  };

  const applyCouponCode = (code: string, type?: CouponType, cartType: CartType = CartType.ECOMMERCE) => {
    dispatch(applyCoupon({ data: { code, type }, cartType }));
  };

  const removeCouponCode = (cartType: CartType = CartType.ECOMMERCE) => {
    dispatch(removeCoupon(cartType));
  };

  const applyReferralCode = (invitationCode: string, cartType: CartType = CartType.ECOMMERCE) => {
    dispatch(applyReferral({ data: { invitationCode }, cartType }));
  };

  const removeReferralCode = (cartType: CartType = CartType.ECOMMERCE) => {
    dispatch(removeReferral(cartType));
  };

  const applySalarCodeValue = (
    code: string,
    commissionType?: string,
    cartType: CartType = CartType.ECOMMERCE,
  ) => {
    dispatch(applySalarCode({ data: { code, commissionType }, cartType }));
  };

  const removeSalarCodeValue = (cartType: CartType = CartType.ECOMMERCE) => {
    dispatch(removeSalarCode(cartType));
  };

  const fetchSavedForLater = (cartType: CartType = CartType.ECOMMERCE) => {
    if (!isAuthenticated) return;
    dispatch(getSavedForLater(cartType));
  };

  const saveItemForLater = async (
    itemId: string,
    cartType: CartType = CartType.ECOMMERCE
  ) => {
    // Resolve any of the loose id formats to the real server cart-item id
    const targetItem = cartItems.find(
      (i) =>
        i.id === itemId ||
        i.productId === itemId ||
        (i.variantId && i.variantId === itemId) ||
        `${i.productId}_${i.variantId || "default"}` === itemId
    );
    const prodId = targetItem?.productId || itemId;
    const varId = targetItem?.variantId || null;
    const localKey = `${prodId}_${varId || "default"}`;
    const targetItemIds = new Set(
      [itemId, targetItem?.id, prodId, localKey].filter(Boolean) as string[]
    );

    const serverItem = (cart?.items || []).find(
      (si) =>
        !si.savedForLater &&
        (si.id === itemId ||
          targetItemIds.has(si.id) ||
          (si.productId === prodId && (si.variantId || null) === varId))
    );

    if (!serverItem?.id) {
      console.warn("saveItemForLater: item not found in server cart", itemId);
      return null;
    }

    // Drop any optimistic local copy so it doesn't reappear in the active list
    setLocalItems((prev) => {
      const updated = prev.filter(
        (item) =>
          !targetItemIds.has(item.id) &&
          !targetItemIds.has(item.productId) &&
          !(item.productId === prodId && (item.variantId || null) === varId)
      );
      try {
        localStorage.setItem("local_cart_items", JSON.stringify(updated));
      } catch {}
      return updated;
    });

    return dispatch(saveForLaterItem({ itemId: serverItem.id, cartType })).unwrap();
  };

  const moveSavedItemToCart = async (
    itemId: string,
    cartType: CartType = CartType.ECOMMERCE
  ) => {
    const targetItem = savedForLaterItems.find(
      (i) =>
        i.id === itemId ||
        i.productId === itemId ||
        (i.variantId && i.variantId === itemId) ||
        `${i.productId}_${i.variantId || "default"}` === itemId
    );

    if (!targetItem?.id) {
      throw new Error("Saved item not found");
    }

    return dispatch(moveSavedToCart({ itemId: targetItem.id, cartType })).unwrap();
  };

  const removeSavedItem = async (itemId: string) => {
    const targetItem = savedForLaterItems.find(
      (i) =>
        i.id === itemId ||
        i.productId === itemId ||
        (i.variantId && i.variantId === itemId) ||
        `${i.productId}_${i.variantId || "default"}` === itemId
    );

    if (!targetItem?.id) {
      throw new Error("Saved item not found");
    }

    return dispatch(removeSavedForLaterItem(targetItem.id)).unwrap();
  };

  const clearCartItems = (cartType: CartType = CartType.ECOMMERCE) => {
    setRemovedItemIds([]);
    setLocalItems([]);
    try {
      localStorage.removeItem("local_cart_items");
    } catch {}
    dispatch(clearCart(cartType));
  };

  const clearMessage = () => {
    dispatch(clearCartState());
  };

  const isProductInCart = useCallback(
    (productId: string | number, variantId?: string): boolean => {
      return cartItems.some((item) => {
        const matchProduct = String(item.productId) === String(productId);
        if (!matchProduct) return false;
        if (variantId !== undefined) {
          return item.variantId === variantId;
        }
        return true;
      });
    },
    [cartItems]
  );

  return (
    <CartContext.Provider
      value={{
        cart: effectiveCart,
        cartItems,
        loading,
        error,
        success,
        message,
        cartCount,
        cartTotal,
        cartOpen,
        setCartOpen,
        isCartOpen: cartOpen,
        setIsCartOpen: setCartOpen,
        fetchCart,
        addItemToCart,
        addToCart: addToCartAction,
        updateItemQuantity,
        removeItemFromCart,
        applyVoucherCode,
        applyCouponCode,
        removeCouponCode,
        applyReferralCode,
        removeReferralCode,
        applySalarCodeValue,
        removeSalarCodeValue,
        clearCartItems,
        clearMessage,
        isProductInCart,
        savedForLaterItems,
        savedForLaterCount: savedForLaterItems.length,
        fetchSavedForLater,
        saveItemForLater,
        moveSavedItemToCart,
        removeSavedItem,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};

